import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  getDay,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarDays, ArrowLeft, Filter, CheckCircle, XCircle, AlertCircle, User } from 'lucide-react';
import projectService from '../../../../services/projectService';
import taskService from '../../../../services/taskService';
import PageBreadcrumb from '../../../../shared/PageBreadCrumb';
import { getFrequencyColor } from '../../utils/projectAssetCheckpointUtils';
import { toast } from 'react-toastify';
import { ResolvedImage } from '../../../../components/media/ResolvedImage';

const FREQUENCY_ORDER = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'];

interface DaySummary {
  pass: number;
  fail: number;
  issueCount: number;
  total: number;
}

interface TaskRecord {
  _id: string;
  startDate?: string;
  dueDate?: string;
  frequency?: string;
  checkpointDetails?: { name?: string; frequency?: string };
  checkpointName?: string;
  status?: string;
  outcome?: string;
  issue?: { description?: string; status?: string };
  equipmentId?: { equipmentName?: string; equipmentId?: string };
  assignedEngineerId?: { _id?: string; name?: string; profilePhoto?: string };
}

function normalizeFrequency(freq: string | undefined): string {
  if (!freq) return 'Other';
  const f = freq.trim();
  if (f === 'Annual') return 'Yearly';
  return f;
}

function dateToKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function ResourceAvatar({ name, profilePhoto, size = 'md' }: { name?: string; profilePhoto?: string; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-9 w-9 text-xs';
  if (profilePhoto) {
    return (
      <ResolvedImage
        mediaSrc={profilePhoto}
        alt={name ?? ''}
        className={`${size === 'sm' ? 'h-7 w-7' : 'h-9 w-9'} flex-shrink-0 rounded-full border border-gray-200 object-cover`}
      />
    );
  }
  const initial = name ? name.trim().charAt(0).toUpperCase() : '?';
  return (
    <div
      className={`${sizeClass} rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-semibold flex-shrink-0 border border-sky-200`}
      title={name ?? ''}
    >
      {initial}
    </div>
  );
}

export default function ProjectPlanningCalendar() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<{
    projectName?: string;
    projectId?: string;
    startDate?: string;
    endDate?: string;
  } | null>(null);
  const [calendarByDate, setCalendarByDate] = useState<Record<string, { frequencyCounts: Record<string, number>; summary: DaySummary; tasks: TaskRecord[] }>>({});
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedFrequencies, setSelectedFrequencies] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedFrequencies(new Set());
  }, [selectedDate]);

  const projectStart = useMemo(() => {
    if (!project?.startDate) return undefined;
    const d = new Date(project.startDate);
    return isNaN(d.getTime()) ? undefined : startOfDay(d);
  }, [project?.startDate]);

  const projectEnd = useMemo(() => {
    if (!project?.endDate) return undefined;
    const d = new Date(project.endDate);
    return isNaN(d.getTime()) ? undefined : startOfDay(d);
  }, [project?.endDate]);

  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await projectService.getProjectById(projectId);
      const data = res?.data ?? res;
      setProject({
        projectName: data?.projectName ?? data?.name,
        projectId: data?.projectId,
        startDate: data?.actualStartDate ?? data?.startDate ?? data?.plannedStartDate,
        endDate: data?.actualEndDate ?? data?.endDate ?? data?.plannedEndDate,
      });
    } catch (e: unknown) {
      toast.error((e as Error)?.message ?? 'Failed to load project');
      setProject(null);
    }
  }, [projectId]);

  const fetchCalendarMonth = useCallback(async () => {
    if (!projectId || !projectStart) return;
    const monthStart = startOfMonth(currentMonth);
    let monthEnd = endOfMonth(currentMonth);
    if (projectEnd && monthEnd > projectEnd) monthEnd = projectEnd;
    setLoading(true);
    try {
      const res = await taskService.getCalendarMonth(
        projectId,
        monthStart.toISOString(),
        monthEnd.toISOString()
      );
      const data = res?.data ?? res;
      setCalendarByDate(data?.byDate ?? {});
    } catch (e: unknown) {
      toast.error((e as Error)?.message ?? 'Failed to load calendar');
      setCalendarByDate({});
    } finally {
      setLoading(false);
    }
  }, [projectId, projectStart, projectEnd, currentMonth]);

  useEffect(() => {
    void fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    if (project?.startDate) void fetchCalendarMonth();
  }, [project?.startDate, fetchCalendarMonth]);

  const tasksByDateKey = useMemo(() => {
    const map = new Map<string, TaskRecord[]>();
    Object.entries(calendarByDate).forEach(([key, val]) => map.set(key, val.tasks ?? []));
    return map;
  }, [calendarByDate]);

  const countsByDateKey = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    Object.entries(calendarByDate).forEach(([key, val]) => map.set(key, val.frequencyCounts ?? {}));
    return map;
  }, [calendarByDate]);

  const summaryByDateKey = useMemo(() => {
    const map = new Map<string, DaySummary>();
    Object.entries(calendarByDate).forEach(([key, val]) =>
      map.set(key, val.summary ?? { pass: 0, fail: 0, issueCount: 0, total: 0 })
    );
    return map;
  }, [calendarByDate]);

  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const padStart = new Date(start);
    padStart.setDate(padStart.getDate() - getDay(start));
    const padEnd = new Date(end);
    padEnd.setDate(padEnd.getDate() + (6 - getDay(end)));
    return eachDayOfInterval({ start: padStart, end: padEnd });
  }, [currentMonth]);

  const canPrev = useMemo(() => {
    if (!projectStart) return true;
    const prevMonth = subMonths(currentMonth, 1);
    return endOfMonth(prevMonth) >= projectStart;
  }, [currentMonth, projectStart]);

  const canNext = useMemo(() => {
    if (!projectEnd) return true;
    const nextMonth = addMonths(currentMonth, 1);
    return startOfMonth(nextMonth) <= projectEnd;
  }, [currentMonth, projectEnd]);

  const dayTasks = useMemo(() => {
    if (!selectedDate) return [];
    return tasksByDateKey.get(dateToKey(selectedDate)) ?? [];
  }, [selectedDate, tasksByDateKey]);

  const daySummary = useMemo(() => {
    if (!selectedDate) return undefined;
    return summaryByDateKey.get(dateToKey(selectedDate));
  }, [selectedDate, summaryByDateKey]);

  const dayFrequencies = useMemo(() => {
    const set = new Set<string>();
    dayTasks.forEach((t) => set.add(normalizeFrequency(t.frequency ?? t.checkpointDetails?.frequency)));
    return FREQUENCY_ORDER.filter((f) => set.has(f)).concat([...set].filter((f) => !FREQUENCY_ORDER.includes(f)));
  }, [dayTasks]);

  const filteredDayTasks = useMemo(() => {
    if (selectedFrequencies.size === 0) return dayTasks;
    return dayTasks.filter((t) => {
      const freq = normalizeFrequency(t.frequency ?? t.checkpointDetails?.frequency);
      return selectedFrequencies.has(freq);
    });
  }, [dayTasks, selectedFrequencies]);

  const toggleFrequency = (freq: string) => {
    setSelectedFrequencies((prev) => {
      const next = new Set(prev);
      if (next.has(freq)) next.delete(freq);
      else next.add(freq);
      return next;
    });
  };

  const getCountForDay = (date: Date) => countsByDateKey.get(dateToKey(date)) ?? {};
  const getSummaryForDay = (date: Date) => summaryByDateKey.get(dateToKey(date));
  const isInRange = (date: Date) => {
    if (!projectStart || !projectEnd) return true;
    return isWithinInterval(date, { start: projectStart, end: projectEnd });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <PageBreadcrumb
        pageTitle="Planning Calendar"
        items={[
          { name: 'Projects', path: '/projects' },
          { name: project?.projectName ?? projectId ?? 'Project', path: `/projects/view/${projectId}` },
          { name: 'Calendar' },
        ]}
      />

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Calendar panel */}
        <div className="flex-1 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-sky-600" />
              <h2 className="font-semibold text-gray-900">
                {project?.projectName ?? 'Project'} — Month view
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <button
                type="button"
                disabled={!canPrev}
                onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[140px] text-center text-sm font-medium text-gray-800">
                {format(currentMonth, 'MMMM yyyy')}
              </span>
              <button
                type="button"
                disabled={!canNext}
                onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-sky-500 border-t-transparent" />
            </div>
          ) : (
            <>
              <div className="p-4">
                <div className="grid grid-cols-7 gap-0.5">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div
                      key={day}
                      className="py-1.5 text-center text-xs font-semibold text-gray-500 uppercase"
                    >
                      {day}
                    </div>
                  ))}
                  {calendarDays.map((date) => {
                    const inMonth = isSameMonth(date, currentMonth);
                    const inRange = isInRange(date);
                    const counts = getCountForDay(date);
                    const summary = getSummaryForDay(date);
                    const total = Object.values(counts).reduce((a, b) => a + b, 0);
                    const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
                    const isToday = isSameDay(date, new Date());

                    return (
                      <button
                        key={date.getTime()}
                        type="button"
                        disabled={!inRange}
                        onClick={() => inRange && setSelectedDate(date)}
                        className={`
                          min-h-[80px] p-1.5 rounded-lg border text-left transition-all
                          ${!inMonth ? 'bg-gray-50/50 text-gray-400' : 'bg-white'}
                          ${!inRange ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-sky-50'}
                          ${isSelected ? 'ring-2 ring-sky-500 bg-sky-50' : 'border-gray-100'}
                          ${isToday && inMonth ? 'border-sky-200' : ''}
                        `}
                      >
                        <span
                          className={`
                            inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium
                            ${!inMonth ? 'text-gray-400' : isToday ? 'bg-sky-600 text-white' : 'text-gray-700'}
                            ${isSelected ? 'bg-sky-600 text-white' : ''}
                          `}
                        >
                          {format(date, 'd')}
                        </span>
                        {inMonth && inRange && total > 0 && (
                          <>
                            <div className="mt-1 flex flex-wrap gap-0.5">
                              {FREQUENCY_ORDER.filter((f) => (counts[f] ?? 0) > 0).map((freq) => {
                                const c = getFrequencyColor(freq);
                                const n = counts[freq] ?? 0;
                                return (
                                  <span
                                    key={freq}
                                    className={`inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium ${c.bg} ${c.text}`}
                                    title={`${freq}: ${n}`}
                                  >
                                    {n}
                                  </span>
                                );
                              })}
                              {Object.entries(counts)
                                .filter(([f]) => !FREQUENCY_ORDER.includes(f))
                                .map(([freq, n]) => {
                                  const c = getFrequencyColor(freq);
                                  return (
                                    <span
                                      key={freq}
                                      className={`inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium ${c.bg} ${c.text}`}
                                      title={`${freq}: ${n}`}
                                    >
                                      {n}
                                    </span>
                                  );
                                })}
                            </div>
                            {summary && (summary.pass > 0 || summary.fail > 0 || summary.issueCount > 0) && (
                              <div className="mt-1 flex items-center gap-1 text-[10px] text-gray-600">
                                {summary.pass > 0 && (
                                  <span className="text-green-600" title="Pass">
                                    {summary.pass}✓
                                  </span>
                                )}
                                {summary.fail > 0 && (
                                  <span className="text-red-600" title="Fail">
                                    {summary.fail}✗
                                  </span>
                                )}
                                {summary.issueCount > 0 && (
                                  <span className="text-amber-600" title="Issues">
                                    {summary.issueCount}⚠
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center gap-3 text-xs">
                <span className="font-medium text-gray-600">Frequency:</span>
                {FREQUENCY_ORDER.map((freq) => {
                  const c = getFrequencyColor(freq);
                  return (
                    <span key={freq} className={`inline-flex items-center gap-1 rounded px-2 py-1 ${c.bg} ${c.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                      {freq}
                    </span>
                  );
                })}
                <span className="ml-2 text-gray-500">✓ Pass · ✗ Fail · ⚠ Issue</span>
              </div>
            </>
          )}
        </div>

        {/* Detail panel */}
        <div className="w-full lg:w-[400px] flex-shrink-0 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-2 text-gray-700">
              <Filter className="h-4 w-4" />
              <span className="font-semibold">
                {selectedDate ? format(selectedDate, 'EEE, MMM d, yyyy') : 'Select a date'}
              </span>
            </div>
            {selectedDate && daySummary && (daySummary.pass > 0 || daySummary.fail > 0 || daySummary.issueCount > 0) && (
              <div className="mt-3 flex items-center gap-4 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 text-sm">
                {daySummary.pass > 0 && (
                  <span className="inline-flex items-center gap-1 text-green-700">
                    <CheckCircle className="h-4 w-4" />
                    <strong>{daySummary.pass}</strong> Pass
                  </span>
                )}
                {daySummary.fail > 0 && (
                  <span className="inline-flex items-center gap-1 text-red-700">
                    <XCircle className="h-4 w-4" />
                    <strong>{daySummary.fail}</strong> Fail
                  </span>
                )}
                {daySummary.issueCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <AlertCircle className="h-4 w-4" />
                    <strong>{daySummary.issueCount}</strong> Issue(s)
                  </span>
                )}
              </div>
            )}
            {selectedDate && dayFrequencies.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {dayFrequencies.map((freq) => {
                  const c = getFrequencyColor(freq);
                  const active = selectedFrequencies.size === 0 || selectedFrequencies.has(freq);
                  return (
                    <button
                      key={freq}
                      type="button"
                      onClick={() => toggleFrequency(freq)}
                      className={`
                        inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors
                        ${active ? `${c.bg} ${c.text} ${c.border} border` : 'bg-gray-100 text-gray-500 border-gray-200'}
                      `}
                    >
                      <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                      {freq}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="p-4 overflow-y-auto flex-1 min-h-0">
            {!selectedDate && (
              <p className="text-sm text-gray-500">Click a date on the calendar to see tasks.</p>
            )}
            {selectedDate && filteredDayTasks.length === 0 && (
              <p className="text-sm text-gray-500">No tasks for this date.</p>
            )}
            {selectedDate && filteredDayTasks.length > 0 && (
              <ul className="space-y-2">
                {filteredDayTasks.map((task) => {
                  const freq = normalizeFrequency(task.frequency ?? task.checkpointDetails?.frequency);
                  const fc = getFrequencyColor(freq);
                  const name = task.checkpointName ?? task.checkpointDetails?.name ?? 'Task';
                  const equip =
                    task.equipmentId && typeof task.equipmentId === 'object'
                      ? (task.equipmentId as { equipmentName?: string; equipmentId?: string }).equipmentName ??
                        (task.equipmentId as { equipmentName?: string; equipmentId?: string }).equipmentId
                      : '';
                  const status = task.status ?? '';
                  const assignee = task.assignedEngineerId;
                  const hasIssue = !!(task.issue && (task.issue.description || task.issue.status));

                  return (
                    <li
                      key={task._id}
                      className={`rounded-lg border p-3 ${fc.border} border-l-4 bg-white`}
                    >
                      <div className="flex items-start gap-2">
                        <ResourceAvatar
                          name={assignee?.name}
                          profilePhoto={assignee?.profilePhoto}
                          size="md"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                            <span
                              className={`
                                flex-shrink-0 rounded px-2 py-0.5 text-[10px] font-medium
                                ${status === 'pass' ? 'bg-green-100 text-green-700' : ''}
                                ${status === 'fail' ? 'bg-red-100 text-red-700' : ''}
                                ${status === 'due' || status === 'overdue' ? 'bg-amber-100 text-amber-700' : ''}
                                ${status === 'inprogress' ? 'bg-blue-100 text-blue-700' : ''}
                                ${status === 'hold' ? 'bg-yellow-100 text-yellow-700' : ''}
                                ${!['pass', 'fail', 'due', 'overdue', 'inprogress', 'hold'].includes(status) ? 'bg-gray-100 text-gray-600' : ''}
                              `}
                            >
                              {status || '—'}
                            </span>
                          </div>
                          {equip && (
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{equip}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${fc.bg} ${fc.text}`}>
                              {freq}
                            </span>
                            {assignee?.name && (
                              <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                                <User className="h-3 w-3" />
                                {assignee.name}
                              </span>
                            )}
                            {hasIssue && (
                              <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800" title="Has issue">
                                <AlertCircle className="h-3 w-3" />
                                Issue
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
